// 2026-09-22 신규 · 2026-10-08 A안 「잠금 카드」로 개편 (대표 결정 2026-10-08 · 시안 docs/design/naver-revenue-gate-variants.html A).
// 마감(hard_deadline_at)을 넘긴 open·rejected 네이버 클립 수익 카드가 있으면 제출 전까지 레이블리 이용을 막는 전면 모달.
// 판정은 서버 my_naver_revenue_gate() 만 — 화면은 gated 값을 그대로 따른다(시각 판정 · rejected 포함 · 2026-10-08 마이그레이션).
// UserLayout 에 한 번만 마운트한다(크리에이터 레이아웃 · Admin/Platform 제외). 닫기 버튼·바깥 클릭·ESC 없음. 「캡처 올리기」로 시트를 열고,
// 제출이 끝나면 refresh() → gated 가 false 가 되면 스스로 사라진다. 잠긴 카드가 여럿이면 첫 카드부터 차례로.
// 문구: 「미지급」은 카드가 forfeited 일 때만(약관 제28조 적용 회차). 그 전 회차는 「올리면 바로 풀려요」만 말한다. 🔒 규칙 코드·단가 비노출.
// 예외 경로(2026-10-09 QA): /support 에서는 띄우지 않는다 — 카드의 「문의하기」가 /support 로 보내는데 전면 모달이 그 화면까지 덮으면 문의 자체가 막힌다.
import React, { useMemo, useState } from "react";
import { Box, Flex, Text, Button, Link, Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import useNaverRevenueCards from "../../hooks/useNaverRevenueCards";
import { monthLabel, STATUS } from "../../lib/naverRevenue";
import { tt } from "../../i18n/tt";
import NaverRevenueSheet from "./NaverRevenueSheet";
import NaverRowEquation from "./NaverRowEquation";

/** 잠금 카드 문안 — 반려(다시 올려 주세요)와 미제출(아직 못 받았어요)을 가른다 */
export function gateCopy(card) {
  if (!card) return null;
  const month = monthLabel(card.revenue_month);
  const isRejected = card.status === STATUS.rejected;
  const title = isRejected
    ? tt("a.8fbc9b2e", "{{v0}} 네이버 클립 수익을 다시 올려 주세요", { v0: month })
    : tt("a.10553cfb", "{{v0}} 네이버 클립 수익을 아직 못 받았어요", { v0: month });
  const reason = isRejected && card.creator_message
    ? card.creator_message
    : isRejected
      ? tt("a.6f3a8c12", "먼저 올리신 캡처는 네이버가 확정하기 전 금액이었어요.")
      : null;
  const unlock = card.forfeited
    ? tt("a.21862b28", "마감이 지나 이 달 정산은 지급되지 않아요. 지금 올려 주시면 레이블리를 다시 이용할 수 있어요.")
    : tt("a.9a4e7d20", "올리시면 바로 레이블리를 다시 이용할 수 있어요.");
  return { title, reason, unlock, isRejected };
}

/** 잠겨 있어도 열려야 하는 경로 — 문의하기(/support, /support/:id). 늘리면 docs/pages/main/NaverRevenueCard.md 「게이트」도 같이 */
export const GATE_EXEMPT_PATHS = ["/support"];
export function isGateExemptPath(pathname) {
  const p = String(pathname || "");
  return GATE_EXEMPT_PATHS.some((base) => p === base || p.startsWith(`${base}/`));
}

export default function NaverRevenueGate({ email }) {
  const { cards, gate, refresh } = useNaverRevenueCards(email);
  const { pathname } = useLocation();
  // 시트에 넘긴 카드는 여기 붙잡아 둔다 — 제출 뒤 refresh 로 gated 가 false 가 돼 게이트가 사라져도 「제출했어요」 화면은 닫을 때까지 남는다.
  const [activeCard, setActiveCard] = useState(null);

  // 시트에는 month_views·prev_amount·creator_message 가 있는 전체 행이 필요하다 — cards 에서 찾고, 없으면 게이트가 준 요약 행으로.
  const first = useMemo(() => {
    const g = gate?.cards?.[0];
    if (!g) return null;
    const full = (cards || []).find((c) => c.id === g.id);
    return full ? { ...full, ...g, creator_message: full.creator_message ?? g.creator_message ?? null } : { ...g, status: g.status || "open", overdue_at: g.overdue_at || new Date(0).toISOString() };
  }, [gate, cards]);

  if (!email) return null;
  if (isGateExemptPath(pathname)) return null;
  const showGate = Boolean(gate?.gated && first);
  if (!showGate && !activeCard) return null;
  const copy = gateCopy(first);
  const more = gate?.cards?.length > 1 ? gate.cards.length - 1 : 0;

  return (
    <>
      {showGate && copy && (
      <Modal isOpen onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} isCentered size={{ base: "full", md: "md" }} blockScrollOnMount scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent borderRadius={{ base: 0, md: "12px" }} data-testid="naver-revenue-gate" data-naver-revenue-gate data-naver-revenue-gate-kind={copy.isRejected ? "rejected" : "open"}>
          <ModalBody px={{ base: 5, md: 7 }} py={{ base: 7, md: 7 }} display="flex" flexDirection="column" justifyContent="center" minH={{ base: "100dvh", md: "auto" }}>
            <Flex direction="column" gap={3}>
              <Flex align="center" gap={2} fontSize="xs" fontWeight="800" color="platform.naver" letterSpacing="0.04em">
                <Box w="8px" h="8px" borderRadius="full" bg="platform.naver" />
                <Text as="span" color="neutral.700">{tt("a.3b9d1f6e", "네이버 클립 · {{v0}} 수익", { v0: monthLabel(first.revenue_month) })}</Text>
              </Flex>
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="800" color="neutral.900" lineHeight="1.3" letterSpacing="-0.02em">
                {copy.title}
              </Text>
              <NaverRowEquation card={first} />
              <Text fontSize="sm" color="neutral.600" lineHeight="1.6">
                {copy.reason ? `${copy.reason} ` : ""}{copy.unlock}
              </Text>
              {/* 2026-10-13 — 찍을 화면은 다음 창(① 상황 고르기)에서 상황별로 알려 준다. 여기서 「정산 완료 내역」 한 장만 보이면 이월 달 크리에이터가 헷갈린다 */}
              <Text fontSize="xs" color="neutral.600" lineHeight="1.6" data-testid="naver-revenue-gate-next">
                {tt("a.nv13k02", "다음 화면에서 네이버 클립 앱의 상황(정산 완료 · 지급 예정 · 5만원 미만 이월)을 고르면 찍을 화면을 알려 드려요.")}
              </Text>
              <Flex align="center" gap={2} wrap="wrap">
                {first.channel_name && (
                  <Box bg="neutral.50" border="1px solid" borderColor="neutral.200" borderRadius="full" px={3} py={1} fontSize="xs" color="neutral.600" display="inline-flex" alignItems="center" gap={1.5}>
                    <Box w="7px" h="7px" borderRadius="full" bg="platform.naver" />
                    {tt("a.e3bd4bc8", "채널 {{v0}}", { v0: first.channel_name })}
                    {more > 0 && ` · ${tt("a.981f7206", "외 {{v0}}건", { v0: more })}`}
                  </Box>
                )}
              </Flex>
              <Button colorScheme="brand" size="lg" w="100%" mt={1} onClick={() => setActiveCard(first)} data-testid="naver-revenue-gate-cta">
                {tt("a.2a044894", "캡처 올리기")}
              </Button>
              <Text fontSize="xs" color="neutral.500" textAlign="center">
                {tt("a.b2e0c7a4", "올리면 바로 풀려요")}
                {" · "}
                <Link as={RouterLink} to="/support" textDecoration="underline">{tt("a.4f1a014e", "문의하기")}</Link>
              </Text>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      )}
      <NaverRevenueSheet
        isOpen={!!activeCard}
        card={activeCard}
        onClose={() => setActiveCard(null)}
        onSubmitted={refresh}
      />
    </>
  );
}
